import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  VerifiedUser as VerifiedUserIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  HourglassTop as HourglassIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentCheckIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';

import { AppDispatch, RootState } from '../../store';
import {
  fetchMerchants,
  reviewMerchant,
  reviewMerchantDocument,
  suspendMerchant,
  Merchant,
  MerchantStatus,
} from '../../store/slices/adminSlice';

// Mock data for merchants (for demonstration)

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`merchant-tabpanel-${index}`}
      aria-labelledby={`merchant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ReviewDialogState {
  open: boolean;
  merchant: Merchant | null;
  rejectionReason: string;
  showRejectReason: boolean;
  rejectingDocument: string | null;
  documentRejectionReason: string;
}

const MerchantApprovalPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  const { merchants, merchantsLoading } = useSelector((state: RootState) => state.admin);

  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [reviewDialog, setReviewDialog] = useState<ReviewDialogState>({
    open: false,
    merchant: null,
    rejectionReason: '',
    showRejectReason: false,
    rejectingDocument: null,
    documentRejectionReason: '',
  });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch merchants from API on mount
  useEffect(() => {
    dispatch(fetchMerchants({ page: 1, limit: 50 }));
  }, [dispatch]);

  // Use live data from API
  const displayMerchants = merchants;

  // Filter merchants by status
  const pendingMerchants = displayMerchants.filter((m) => m.status === 'pending');
  const approvedMerchants = displayMerchants.filter((m) => m.status === 'approved');
  const rejectedMerchants = displayMerchants.filter((m) => m.status === 'rejected');
  const suspendedMerchants = displayMerchants.filter((m) => m.status === 'suspended');

  // Get merchants to display based on tab
  const getMerchantsForTab = () => {
    switch (tabValue) {
      case 0:
        return displayMerchants;
      case 1:
        return pendingMerchants;
      case 2:
        return approvedMerchants;
      case 3:
        return rejectedMerchants;
      case 4:
        return suspendedMerchants;
      default:
        return displayMerchants;
    }
  };

  const currentTabMerchants = getMerchantsForTab();

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status chip color
  const getStatusColor = (status: MerchantStatus) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: MerchantStatus) => {
    switch (status) {
      case 'pending':
        return <HourglassIcon sx={{ fontSize: '1rem', mr: 0.5 }} />;
      case 'approved':
        return <CheckCircleIcon sx={{ fontSize: '1rem', mr: 0.5 }} />;
      case 'rejected':
        return <CancelIcon sx={{ fontSize: '1rem', mr: 0.5 }} />;
      case 'suspended':
        return <BlockIcon sx={{ fontSize: '1rem', mr: 0.5 }} />;
      default:
        return undefined;
    }
  };

  // Open review dialog
  const handleOpenReview = (merchant: Merchant) => {
    setReviewDialog({
      open: true,
      merchant,
      rejectionReason: merchant.rejectionReason || '',
      showRejectReason: false,
      rejectingDocument: null,
      documentRejectionReason: '',
    });
  };

  // Close review dialog
  const handleCloseReview = () => {
    setReviewDialog({
      open: false,
      merchant: null,
      rejectionReason: '',
      showRejectReason: false,
      rejectingDocument: null,
      documentRejectionReason: '',
    });
  };

  const refreshMerchants = () => dispatch(fetchMerchants({ page: 1, limit: 50 }));

  const runMerchantAction = async (
    label: string,
    action: () => Promise<unknown>,
  ) => {
    if (!reviewDialog.merchant) return;
    setActionLoading(reviewDialog.merchant.id);
    setActionError(null);
    try {
      await action();
      setSuccessMessage(`${reviewDialog.merchant.businessName} ${label}.`);
      await refreshMerchants();
      setTimeout(() => {
        handleCloseReview();
        setSuccessMessage(null);
      }, 1500);
    } catch (error: any) {
      setActionError(typeof error === 'string' ? error : error?.message || `Failed: ${label}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle approve merchant
  const handleApproveMerchant = async () => {
    if (!reviewDialog.merchant) return;
    const id = reviewDialog.merchant.id;
    await runMerchantAction('has been approved', () =>
      dispatch(reviewMerchant({ id, status: 'approved' })).unwrap()
    );
  };

  // Handle reject merchant
  const handleRejectMerchant = async () => {
    if (!reviewDialog.merchant || !reviewDialog.rejectionReason) {
      alert('Please provide a rejection reason');
      return;
    }
    const id = reviewDialog.merchant.id;
    const rejectionReason = reviewDialog.rejectionReason;
    await runMerchantAction('has been rejected', () =>
      dispatch(reviewMerchant({ id, status: 'rejected', rejectionReason })).unwrap()
    );
  };

  // Handle suspend merchant
  const handleSuspendMerchant = async () => {
    if (!reviewDialog.merchant || !reviewDialog.rejectionReason) {
      alert('Please provide a suspension reason');
      return;
    }
    const id = reviewDialog.merchant.id;
    const reason = reviewDialog.rejectionReason;
    await runMerchantAction('has been suspended', () =>
      dispatch(suspendMerchant({ id, reason })).unwrap()
    );
  };

  // Handle approve document
  const handleApproveDocument = async (docId: string) => {
    setActionError(null);
    try {
      await dispatch(reviewMerchantDocument({ docId, status: 'approved' })).unwrap();
      setSuccessMessage('Document approved.');
      await refreshMerchants();
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error: any) {
      setActionError(typeof error === 'string' ? error : 'Failed to approve document');
    }
  };

  // Handle reject document
  const handleRejectDocument = async (docId: string) => {
    const rejectionReason = window.prompt('Reason for rejecting this document:');
    if (!rejectionReason) return;
    setActionError(null);
    try {
      await dispatch(reviewMerchantDocument({ docId, status: 'rejected', rejectionReason })).unwrap();
      setSuccessMessage('Document rejected.');
      await refreshMerchants();
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error: any) {
      setActionError(typeof error === 'string' ? error : 'Failed to reject document');
    }
  };

  // Get document status color
  const getDocumentStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: '2rem' }} />
            Merchant Management
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Review and manage merchant applications and accounts
          </Typography>
        </Box>
      </Box>

      {/* Stats Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Merchants
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {displayMerchants.length}
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: '2.5rem', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Pending Review
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {pendingMerchants.length}
                  </Typography>
                </Box>
                <HourglassIcon sx={{ fontSize: '2.5rem', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Approved
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {approvedMerchants.length}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: '2.5rem', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
              color: 'white',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Revenue Potential
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    ₦2.5M
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: '2.5rem', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="merchant tabs"
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: isMobile ? 1 : 3,
          }}
        >
          <Tab
            label="All Merchants"
            id="merchant-tab-0"
            aria-controls="merchant-tabpanel-0"
            icon={<PeopleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Pending Review (${pendingMerchants.length})`}
            id="merchant-tab-1"
            aria-controls="merchant-tabpanel-1"
            icon={
              <Chip
                label={pendingMerchants.length}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            }
            iconPosition="end"
          />
          <Tab
            label={`Approved (${approvedMerchants.length})`}
            id="merchant-tab-2"
            aria-controls="merchant-tabpanel-2"
            icon={
              <Chip
                label={approvedMerchants.length}
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            }
            iconPosition="end"
          />
          <Tab
            label={`Rejected (${rejectedMerchants.length})`}
            id="merchant-tab-3"
            aria-controls="merchant-tabpanel-3"
            icon={
              <Chip
                label={rejectedMerchants.length}
                size="small"
                color="error"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            }
            iconPosition="end"
          />
          <Tab
            label={`Suspended (${suspendedMerchants.length})`}
            id="merchant-tab-4"
            aria-controls="merchant-tabpanel-4"
            icon={
              <Chip
                label={suspendedMerchants.length}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            }
            iconPosition="end"
          />
        </Tabs>

        {/* Merchants Table */}
        <TabPanel value={tabValue} index={0}>
          <MerchantsTable
            merchants={currentTabMerchants}
            loading={merchantsLoading}
            onReview={handleOpenReview}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MerchantsTable
            merchants={currentTabMerchants}
            loading={merchantsLoading}
            onReview={handleOpenReview}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <MerchantsTable
            merchants={currentTabMerchants}
            loading={merchantsLoading}
            onReview={handleOpenReview}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <MerchantsTable
            merchants={currentTabMerchants}
            loading={merchantsLoading}
            onReview={handleOpenReview}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <MerchantsTable
            merchants={currentTabMerchants}
            loading={merchantsLoading}
            onReview={handleOpenReview}
          />
        </TabPanel>
      </Card>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={handleCloseReview}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserIcon color="primary" />
            Merchant Review
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {reviewDialog.merchant && (
            <Stack spacing={2}>
              {/* Business Information */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" />
                  Business Information
                </Typography>
                <Box sx={{ pl: 2, py: 1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Business Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.businessName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Business Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.businessType || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Registration Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.taxId || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.address || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Owner Information */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon fontSize="small" />
                  Owner Information
                </Typography>
                <Box sx={{ pl: 2, py: 1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.email}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {reviewDialog.merchant.phoneNumber}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Current Status */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Current Status
                </Typography>
                <Chip
                  icon={getStatusIcon(reviewDialog.merchant.status)}
                  label={reviewDialog.merchant.status.charAt(0).toUpperCase() + reviewDialog.merchant.status.slice(1)}
                  color={getStatusColor(reviewDialog.merchant.status)}
                  sx={{ fontWeight: 600 }}
                />
                {reviewDialog.merchant.rejectionReason && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <strong>Reason:</strong> {reviewDialog.merchant.rejectionReason}
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Documents */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileDownloadIcon fontSize="small" />
                  Verification Documents
                </Typography>
                <Stack spacing={1}>
                  {reviewDialog.merchant.verificationDocuments?.map((doc: any) => (
                    <Box
                      key={doc.id}
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {doc.type}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(doc.uploadDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={getDocumentStatusColor(doc.status)}
                          variant="outlined"
                        />
                        {doc.status === 'pending' && (
                          <>
                            <Tooltip title="Approve document">
                              <IconButton size="small" color="success" onClick={() => handleApproveDocument(doc.id)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject document">
                              <IconButton size="small" color="error" onClick={() => handleRejectDocument(doc.id)}>
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Action Reason (if rejecting/suspending) */}
              {(reviewDialog.merchant.status === 'pending' || reviewDialog.merchant.status === 'approved') && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Decision
                  </Typography>
                  {reviewDialog.showRejectReason && (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={reviewDialog.merchant.status === 'approved' ? 'Reason for Suspension' : 'Reason for Rejection'}
                      placeholder={reviewDialog.merchant.status === 'approved' ? 'Explain why this merchant is being suspended...' : 'Explain why this merchant is being rejected...'}
                      value={reviewDialog.rejectionReason}
                      onChange={(e) =>
                        setReviewDialog((prev) => ({
                          ...prev,
                          rejectionReason: e.target.value,
                        }))
                      }
                      sx={{ mb: 1 }}
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseReview} variant="outlined">
            Close
          </Button>

          {reviewDialog.merchant && reviewDialog.merchant.status === 'pending' && (
            <>
              <Button
                onClick={handleApproveMerchant}
                variant="contained"
                startIcon={<CheckCircleIcon />}
                disabled={actionLoading !== null}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                }}
              >
                {actionLoading === reviewDialog.merchant.id ? <CircularProgress size={20} /> : 'Approve'}
              </Button>

              {!reviewDialog.showRejectReason ? (
                <Button
                  onClick={() =>
                    setReviewDialog((prev) => ({
                      ...prev,
                      showRejectReason: true,
                    }))
                  }
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                >
                  Reject
                </Button>
              ) : (
                <Button
                  onClick={handleRejectMerchant}
                  variant="contained"
                  color="error"
                  disabled={!reviewDialog.rejectionReason || actionLoading !== null}
                  startIcon={<CancelIcon />}
                >
                  {actionLoading === reviewDialog.merchant.id ? <CircularProgress size={20} /> : 'Confirm Reject'}
                </Button>
              )}

            </>
          )}

          {reviewDialog.merchant && reviewDialog.merchant.status === 'approved' && (
            !reviewDialog.showRejectReason ? (
              <Button
                onClick={() => setReviewDialog((prev) => ({ ...prev, showRejectReason: true }))}
                variant="outlined"
                color="warning"
                startIcon={<BlockIcon />}
              >
                Suspend
              </Button>
            ) : (
              <Button
                onClick={handleSuspendMerchant}
                variant="contained"
                color="warning"
                disabled={!reviewDialog.rejectionReason || actionLoading !== null}
                startIcon={<BlockIcon />}
              >
                {actionLoading === reviewDialog.merchant.id ? <CircularProgress size={20} /> : 'Confirm Suspend'}
              </Button>
            )
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Separate Merchants Table Component
interface MerchantsTableProps {
  merchants: Merchant[];
  loading: boolean;
  onReview: (merchant: Merchant) => void;
}

const MerchantsTable: React.FC<MerchantsTableProps> = ({ merchants, loading, onReview }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (merchants.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <InfoIcon sx={{ fontSize: '3rem', color: 'textSecondary', mb: 1 }} />
        <Typography color="textSecondary">No merchants found in this category</Typography>
      </Box>
    );
  }

  if (isMobile) {
    // Mobile view - Card layout
    return (
      <Stack spacing={2}>
        {merchants.map((merchant) => (
          <Card key={merchant.id}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {merchant.businessName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {merchant.businessType}
                  </Typography>
                </Box>
                <Chip
                  label={merchant.status}
                  color={getStatusColor(merchant.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {merchant.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Phone:</strong> {merchant.phoneNumber}
              </Typography>
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => onReview(merchant)}
                startIcon={<InfoIcon />}
              >
                Review
              </Button>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  // Desktop view - Table layout
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
            <TableCell sx={{ fontWeight: 700 }}>Business Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Owner Email</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Business Type</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Registration</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Date Applied</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700 }}>
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {merchants.map((merchant) => (
            <TableRow
              key={merchant.id}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              <TableCell sx={{ fontWeight: 600 }}>{merchant.businessName}</TableCell>
              <TableCell>{merchant.email}</TableCell>
              <TableCell>{merchant.businessType || 'N/A'}</TableCell>
              <TableCell>{merchant.taxId || 'N/A'}</TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(merchant.status)}
                  label={merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1)}
                  color={getStatusColor(merchant.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </TableCell>
              <TableCell>{formatDate(merchant.createdAt)}</TableCell>
              <TableCell align="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onReview(merchant)}
                  startIcon={<InfoIcon />}
                >
                  Review
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Helper function for status color - needed for mobile card view
function getStatusColor(status: MerchantStatus): any {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'suspended':
      return 'default';
    default:
      return 'default';
  }
}

// Helper function for status icon - needed for mobile card view
function getStatusIcon(status: MerchantStatus) {
  switch (status) {
    case 'pending':
      return <HourglassIcon sx={{ fontSize: '1rem' }} />;
    case 'approved':
      return <CheckCircleIcon sx={{ fontSize: '1rem' }} />;
    case 'rejected':
      return <CancelIcon sx={{ fontSize: '1rem' }} />;
    case 'suspended':
      return <BlockIcon sx={{ fontSize: '1rem' }} />;
    default:
      return undefined;
  }
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default MerchantApprovalPage;
